import { pipe, map, fromPairs, values } from 'ramda'
import { EpiBlock, EpiBlockProperties } from '@trr/internaltypes'
import { UserProfileImages } from 'state/PublicWebApi/UserProfile'
import { CardProps } from 'App/components/Card'
import { date as dateFormatter } from '@trr/frontend-datetime'

import { IContentBlock } from 'App/Epi/BlockMapper/BlockMapper'
import { PhotoTextBlockProps } from 'App/Epi/Blocks/PhotoTextBlock/PhotoTextBlock'
import { AuthState } from 'slices/Auth/types'

export const removeTrailingSlash = (url: string): string =>
  url.replace(/\/$/, '')

export const getPageName = (...names: string[]): string =>
  names.length ? `TRR: ${names.join(' - ')}` : 'TRR'

export const dictionaryToObject = pipe<
  Record<string, unknown>[],
  unknown[][],
  Record<string, unknown>
>(map(values), fromPairs)

/**
 * Converts and flattens Epi block to Record, removing child dictionaries
 * @param {EpiBlock} block - block to convert
 * @returns {Record} flattened object
 */
export const filterContentBlockAreaToObject = (
  block: EpiBlock
): Record<string, unknown> => {
  if (
    !block.properties?.contentBlockArea ||
    typeof block.properties.contentBlockArea !== 'object'
  ) {
    return {}
  }

  const keys = Object.values(block.properties.contentBlockArea).map(
    ({ properties }: { properties: EpiBlockProperties }) => {
      const customDictionaryObject = properties.customDictionary && {
        ...dictionaryToObject(properties.customDictionary),
      }
      const customLinkDictionaryObject = properties.customLinkDictionary && {
        ...dictionaryToObject(properties.customLinkDictionary),
      }

      const blockWithOmittedProperties = Object.fromEntries(
        Object.entries(properties).filter(
          ([key]) =>
            !['customDictionary', 'blockKey', 'customLinkDictionary'].includes(
              key
            )
        )
      )

      const blockKey = properties.blockKey

      if (!blockKey) {
        console.error('Block key is missing in properties:', properties)
        return {}
      }

      return {
        [blockKey]: {
          ...customDictionaryObject,
          ...customLinkDictionaryObject,
          ...blockWithOmittedProperties,
        },
      }
    }
  )
  return keys.reduce(
    (contentBlock, currentBlock) => ({ ...contentBlock, ...currentBlock }),
    {}
  )
}

export const getProfileImages = (fileName: string): UserProfileImages => {
  const imageBaseUrl = `${window.APP_CONFIGURATION.COMMON.MEDIA_URL}/user-profile-images/`
  enum UserProfileImageWidths {
    tiny = 32,
    small = 48,
    medium = 80,
    large = 112,
    xlarge = 160,
    xxlarge = 320,
  }

  const defaultProfileImages = {
    tiny: '',
    small: '',
    medium: '',
    large: '',
    xlarge: '',
    xxlarge: '',
  }

  const sizes: UserProfileImages = fileName
    ? Object.keys(defaultProfileImages).reduce(
        (
          result: UserProfileImages,
          currentValue: string
        ): UserProfileImages => {
          const size = currentValue as keyof UserProfileImages
          result[size] =
            `${imageBaseUrl}${fileName}&width=${UserProfileImageWidths[size]}`
          return result
        },
        defaultProfileImages
      )
    : defaultProfileImages

  return sizes
}

export const pageIsSummaryInEnglish = (path: string): boolean => {
  return path?.slice(0, 19) === '/summary-in-english'
}

export const asyncCopyToClipboard = async (text: string) => {
  await navigator.clipboard.writeText(text)
}

export const asyncMobilePageShare = async (url: string) => {
  try {
    await navigator.share({ url: url })
  } catch (error) {
    console.error(error)
  }
}

export const replaceVariablesInText = (
  text: string,
  variables: { [key: string]: string }
): string => {
  const regex = (key: string): RegExp => new RegExp(`{{${key}}}`, 'g')
  Object.keys(variables).forEach(
    (key) => (text = text.replace(regex(key), variables[key]))
  )
  return text
}

export interface PageListItem extends IContentBlock {
  startPublish?: string
  url: string
  properties: {
    customCategory?: string
    puffView?: {
      heading?: string
      longIntro?: string
      mainIntro?: string
      image?: string
    }
    introduction: {
      heading?: string
      text?: string
    }
  }
}

export const convertPageListToCards = (
  pageList: PageListItem[],
  fallbackUrl?: string,
  showCategory?: boolean
): CardProps[] => {
  const cards: CardProps[] = pageList.map(
    ({
      startPublish,
      url,
      properties: { customCategory, puffView, introduction },
    }) => {
      const formattedPublishDate =
        startPublish &&
        dateFormatter.format(startPublish, {
          style: 'numeric',
        })

      const category = customCategory
        ? `${formattedPublishDate} • ${customCategory}`
        : formattedPublishDate

      let preamble =
        puffView?.longIntro || puffView?.mainIntro || introduction?.text

      if (preamble && preamble.length > 120) {
        preamble = preamble.slice(0, 120).concat('...')
      }

      const card: CardProps = {
        heading: (puffView?.heading || introduction?.heading) ?? '',
        preamble: preamble ?? '',
        cardCategory: showCategory ? category : undefined,
        image: puffView?.image || fallbackUrl,
        useImage: true,
        link: { text: 'Läs mer', url: url },
        publishDate: startPublish ?? '',
        category: customCategory,
      }
      return card
    }
  )
  return cards
}

export const convertPageListToPhotoTextBlock = (
  pageList: PageListItem,
  latestNewsCategory?: string | null,
  showCategory?: boolean,
  readMoreText?: string
): PhotoTextBlockProps => {
  const photoTextBlock: PhotoTextBlockProps = {
    content: {
      heading:
        pageList.properties.puffView?.heading ??
        pageList.properties.introduction?.heading ??
        '',
      body:
        pageList.properties.puffView?.longIntro ||
        pageList.properties.puffView?.mainIntro ||
        pageList.properties.introduction?.text,
      customCategory: showCategory
        ? latestNewsCategory || pageList.properties.customCategory
        : undefined,
      image: pageList.properties.puffView?.image,
      link: { text: readMoreText ?? '', url: pageList.url },
      imagePosition: 'left',
      color: 'blue',
      useColor: true,
    },
  }
  return photoTextBlock
}

export const getLoginUrl = (
  idp: string | undefined,
  arbetsgivarinfoLinkId: string | undefined
) => {
  if (arbetsgivarinfoLinkId)
    return `/login/arbetsgivarinfo?linkId=${arbetsgivarinfoLinkId}`

  return idp === 'kund' ? '/login/arbetsgivare' : '/login'
}

export const isOnboardingJwt = (sub: string = '') =>
  /^\d{6,8}[-|(\s)]{0,1}[A-Za-z0-9]{4}$/.test(sub)

export const getPossibleArbetsgivarinfoLinkId = (user: AuthState) => {
  const userState = user?.state?.arbetsgivarinfoLinkId
  const arbetsgivarinfoUrl = new URLSearchParams(window.location.search).get(
    'linkId'
  )

  return userState ?? arbetsgivarinfoUrl ?? undefined
}
